import type { App } from 'vue';
import { reactive, watch } from 'vue';

import type { EventMessage, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { EventMessageUtils, EventType, InteractionStatus } from '@azure/msal-browser';

type AccountIdentifiers = Partial<
  Pick<AccountInfo, 'homeAccountId' | 'localAccountId' | 'username'>
>;

/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */
function accountArraysAreEqual(
  arrayA: Array<AccountIdentifiers>,
  arrayB: Array<AccountIdentifiers>
): boolean {
  if (arrayA.length !== arrayB.length) {
    return false;
  }

  const comparisonArray = [...arrayB];

  return arrayA.every(elementA => {
    const elementB = comparisonArray.shift();
    if (!elementA || !elementB) {
      return false;
    }

    return (
      elementA.homeAccountId === elementB.homeAccountId &&
      elementA.localAccountId === elementB.localAccountId &&
      elementA.username === elementB.username
    );
  });
}

export const msalPlugin = {
  install: (
    app: App,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { authStore }: { authStore: any }
  ) => {
    const msalInstance = authStore.msalInstance;
    const authState = reactive({
      msalInstance,
      accounts: msalInstance.getAllAccounts(),
      inProgress: InteractionStatus.Startup as InteractionStatus,
    });

    if (authState.accounts.length > 0) {
      msalInstance.setActiveAccount(authState.accounts[0]);
    }

    authStore.setMsalInstance(authState.msalInstance);
    authStore.setAccounts(authState.accounts);
    authStore.setInProgress(authState.inProgress);

    watch(authState, newAuthState => {
      authStore.setMsalInstance(newAuthState.msalInstance);
      authStore.setAccounts(newAuthState.accounts);
      authStore.setInProgress(newAuthState.inProgress);
    });

    msalInstance.addEventCallback((message: EventMessage) => {
      let currentAccounts = null;

      if (message.eventType === EventType.LOGIN_SUCCESS && message.payload) {
        const payload = message.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }

      switch (message.eventType) {
        case EventType.ACCOUNT_ADDED:
        case EventType.ACCOUNT_REMOVED:
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
        case EventType.HANDLE_REDIRECT_END:
        case EventType.LOGIN_FAILURE:
        case EventType.SSO_SILENT_FAILURE:
        case EventType.LOGOUT_END:
        case EventType.ACQUIRE_TOKEN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_FAILURE:
          currentAccounts = msalInstance.getAllAccounts();
          if (!accountArraysAreEqual(currentAccounts, authState.accounts)) {
            authState.accounts = currentAccounts;
          }
          break;
      }

      const status = EventMessageUtils.getInteractionStatusFromEvent(message, authState.inProgress);
      console.warn('InteractionStatus :', status);
      if (status !== null) {
        authState.inProgress = status;
      }
    });
  },
};
